/**
 * The models a user may pick, next to the chat input. One choice for the whole
 * Portal (not per agent).
 *
 * Served by the Gateway (GET /channels/portal/models) from the AppConfig
 * `agent_models` flag. The response carries option keys and display labels
 * only, never Bedrock model ids. It is fetched once and cached for the session.
 * The selection is remembered in localStorage and sent as `model` on every chat
 * request; the Gateway validates it, and changing it mid-conversation applies
 * from the next message.
 */
import { gatewayClient } from '../../../client/GatewayClient';
import type { Locale } from '../../../i18n/resources';

export interface ModelLabel {
  zh?: string;
  en?: string;
}

export interface ModelOption {
  key: string;
  label: ModelLabel;
}

export interface ModelChoices {
  default: string;
  options: ModelOption[];
}

const KEY_RE = /^[a-z0-9-]{1,64}$/;
export const STORAGE_KEY = 'rocky:model';

/** Keep only well-formed options; a missing/unknown default falls back to the first option. */
export function parseModelChoices(raw: unknown): ModelChoices | null {
  if (!raw || typeof raw !== 'object') return null;
  const body = raw as { default?: unknown; options?: unknown };
  if (!Array.isArray(body.options)) return null;
  const options: ModelOption[] = [];
  for (const o of body.options) {
    if (!o || typeof o !== 'object') continue;
    const { key, label } = o as { key?: unknown; label?: unknown };
    if (typeof key !== 'string' || !KEY_RE.test(key) || options.some((x) => x.key === key)) continue;
    const l = label && typeof label === 'object' ? (label as Record<string, unknown>) : {};
    options.push({
      key,
      label: {
        ...(typeof l.zh === 'string' ? { zh: l.zh } : {}),
        ...(typeof l.en === 'string' ? { en: l.en } : {}),
      },
    });
  }
  if (options.length === 0) return null;
  const def = typeof body.default === 'string' && options.some((o) => o.key === body.default)
    ? body.default
    : options[0].key;
  return { default: def, options };
}

let cached: Promise<ModelChoices | null> | null = null;

/** The model options, fetched once per session. A failed fetch is retried on the next call. */
export function fetchModelChoices(): Promise<ModelChoices | null> {
  if (!cached) {
    // Start inside the promise so a synchronous client error rejects instead of throwing.
    cached = Promise.resolve()
      .then(() => gatewayClient.get<unknown>('/channels/portal/models'))
      .then(parseModelChoices)
      .catch((err) => {
        cached = null;
        throw err;
      });
  }
  return cached;
}

/** Test hook: forget the cached options. */
export function resetModelChoicesCache(): void {
  cached = null;
}

/** The label in the UI language, else the other language, else the key. */
export function modelLabel(option: ModelOption, locale: Locale): string {
  const other: Locale = locale === 'en' ? 'zh' : 'en';
  return option.label[locale]?.trim() || option.label[other]?.trim() || option.key;
}

/** The stored choice if it is still offered, else the default. */
export function effectiveModel(choices: ModelChoices, stored: string | null): string {
  return stored && choices.options.some((o) => o.key === stored) ? stored : choices.default;
}

/** True when there is a real choice to show. */
export function hasChoice(choices: ModelChoices | null): choices is ModelChoices {
  return !!choices && choices.options.length > 1;
}

export function loadStoredModel(storage: Storage | undefined = safeStorage()): string | null {
  try {
    return storage?.getItem(STORAGE_KEY) ?? null;
  } catch {
    return null;
  }
}

export function saveStoredModel(key: string, storage: Storage | undefined = safeStorage()): void {
  try {
    storage?.setItem(STORAGE_KEY, key);
  } catch {
    // Private mode / quota: the choice just isn't remembered.
  }
}

/**
 * The `model` to send with a chat request: only when the user has a real choice
 * (otherwise the runtime default applies and nothing is sent).
 */
export function modelForRequest(choices: ModelChoices | null, selected: string | null): string | undefined {
  if (!hasChoice(choices) || !selected) return undefined;
  return choices.options.some((o) => o.key === selected) ? selected : undefined;
}

function safeStorage(): Storage | undefined {
  try {
    return typeof window !== 'undefined' ? window.localStorage : undefined;
  } catch {
    return undefined;
  }
}
